import type {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
} from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Risk, Vulnerability } from '@rtkwlf/mr-infra-risk';
import { faRotate } from '@rtkwlf/pro-solid-svg-icons/faRotate';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';

import {
  DrawerType,
  useActionDrawer,
} from '@packages/components/action-drawer/action-drawer-context';
import type { ExportActionProps } from '@packages/components/grid-action-toolbar/export-action';
import { GridActionToolbar } from '@packages/components/grid-action-toolbar/grid-action-toolbar';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowParams,
  GridSortModel,
  GridSlotProps,
  GridApiPro,
} from '@packages/fenrir/components';
import type { GridRowSelectionModel } from '@packages/fenrir/components';
import {
  DataGridPro,
  GridLogicOperator,
  filterColumns,
  getColumnForNewFilter,
  filterFormPropsDefaults,
  Alert,
  Typography,
  initTogglableColumns,
  usePersistedGrid,
  GRID_ACTIONS_COLUMN_TYPE,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_CHECKBOX_SELECTION_FIELD,
} from '@packages/fenrir/components';

export type RiskManagementRow = Risk | Vulnerability;
export interface RiskManagementTableProps<
  T extends RiskManagementRow = RiskManagementRow,
> {
  rows: T[] | undefined;
  columns: GridColDef[];
  columnVisibilityModel: GridColumnVisibilityModel;
  rowExpansionContent: (row: GridRowParams) => ReactNode;
  isLoading: boolean;
  handleOnRowsScrollEnd: () => void;
  sortModel: GridSortModel;
  setSortModel: Dispatch<SetStateAction<GridSortModel>>;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  gridStateKey: string;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectionLimit?: number;
  getHiddenColumns?: (columns: GridColDef[]) => string[];
  updateDrawerComponent: () => JSX.Element;
  getRowId?: (row: T) => string;
  exportModel?: ExportActionProps;
  gridApiRef?: MutableRefObject<GridApiPro | null>;
}

const slots = {
  toolbar: GridActionToolbar,
};

const MAX_MULTI_SORT_LIMIT = 3;
const DEFAULT_SELECTION_LIMIT = 1000;
const DEFAULT_HIDDEN_COLS = initTogglableColumns([]);

export const RiskManagementTable = <T extends RiskManagementRow>({
  rows = [],
  columns,
  columnVisibilityModel,
  rowExpansionContent,
  isLoading,
  handleOnRowsScrollEnd,
  sortModel = [],
  setSortModel,
  filterModel = { items: [] },
  setFilterModel,
  gridStateKey,
  rowSelectionModel,
  setRowSelectionModel,
  selectionLimit = DEFAULT_SELECTION_LIMIT,
  getHiddenColumns,
  updateDrawerComponent,
  getRowId,
  exportModel,
  gridApiRef,
}: RiskManagementTableProps<T>) => {
  const getDetailPanelHeight = useCallback(() => 'auto', []);
  const getRowHeight = useCallback(() => 'auto', []);
  const { openDrawer } = useActionDrawer();
  const { t } = useTranslation('riskManagement', {
    keyPrefix: 'risks',
  });

  const handleUpdateBtnClick = useCallback(() => {
    openDrawer(updateDrawerComponent(), {
      drawerType: DrawerType.FULL_HEIGHT_DRAWER,
    });
  }, [openDrawer, updateDrawerComponent]);

  const gridOptions = useMemo(
    () => ({
      columns,
      defaultState: {
        columns: {
          columnVisibilityModel,
        },
        pinnedColumns: {
          left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, GRID_CHECKBOX_SELECTION_FIELD],
          right: [GRID_ACTIONS_COLUMN_TYPE],
        },
      },
    }),
    [columnVisibilityModel, columns],
  );

  const {
    apiRef,
    isLoading: isPersistenceLoading,
    initialState,
  } = usePersistedGrid({
    persistencyOptions: {
      storageKey: gridStateKey,
      featuresToPersist: {
        density: true,
        columns: {
          columnVisibilityModel: true,
        },
      },
      persistBeforeunload: true,
    },
    gridOptions,
  });

  useEffect(() => {
    if (apiRef.current && gridApiRef) {
      gridApiRef.current = apiRef.current;
    }
  }, [apiRef, gridApiRef]);

  const slotProps: Partial<GridSlotProps> = useMemo(() => {
    return {
      toolbar: {
        showQuickFilter: true,
        printOptions: { disableToolbarButton: true },
        csvOptions: { disableToolbarButton: true },
        selectedRowsCount: rowSelectionModel.length,
        onActionButtonClick: handleUpdateBtnClick,
        onClearButtonClick: () => setRowSelectionModel([]),
        exportProps: exportModel,
        actionBarProps: {
          rowSelection: { count: rowSelectionModel.length },
          actions: [
            {
              label: t('actionDrawerTitle'),
              icon: <FontAwesomeIcon icon={faRotate} aria-label="rotate" />,
              onClick: handleUpdateBtnClick,
            },
          ],
        },
      },
      columnsManagement: {
        defaultColumnVisibilityModel: columnVisibilityModel,
        getTogglableColumns: getHiddenColumns
          ? getHiddenColumns
          : DEFAULT_HIDDEN_COLS,
      },
      filterPanel: {
        logicOperators: [GridLogicOperator.And],
        filterFormProps: {
          ...filterFormPropsDefaults,
          filterColumns,
        },
        getColumnForNewFilter,
      },
    };
  }, [
    rowSelectionModel.length,
    handleUpdateBtnClick,
    columnVisibilityModel,
    getHiddenColumns,
    setRowSelectionModel,
    exportModel,
    t,
  ]);

  const saveSnapshot = useCallback(() => {
    if (isEmpty(apiRef?.current)) {
      return;
    }

    const currentState = apiRef?.current.exportState();

    sessionStorage.setItem(
      gridStateKey,
      JSON.stringify({
        filter: currentState.filter,
        sorting: currentState.sorting,
      }),
    );
  }, [apiRef, gridStateKey]);

  useLayoutEffect(() => {
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  useEffect(() => {
    // use update rows to take advantage of upsert functionality
    if (apiRef?.current?.updateRows) {
      apiRef.current.setRows([]);
      apiRef.current.updateRows(rows.flat());
    }
  }, [rows, apiRef]);

  const handleSortModelChange = useCallback(
    (updatedSortModel: GridSortModel) => {
      const sortCount = updatedSortModel.length;
      if (sortCount > MAX_MULTI_SORT_LIMIT) {
        const newSortModelArr = updatedSortModel.slice(1);
        setSortModel(newSortModelArr);
      } else {
        setSortModel(updatedSortModel);
      }
    },
    [setSortModel],
  );

  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      handleSortModelChange(newSortModel);
    },
    [handleSortModelChange],
  );

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => setFilterModel(newFilterModel),
    [setFilterModel],
  );

  // Row selection state
  const handleRowSelectionModelChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      const updatedSelectionModel =
        newRowSelectionModel.length > selectionLimit
          ? newRowSelectionModel.slice(0, selectionLimit)
          : newRowSelectionModel;
      setRowSelectionModel(updatedSelectionModel);
    },
    [selectionLimit, setRowSelectionModel],
  );

  const isRowSelectable = useCallback(
    (params: GridRowParams) => {
      return (
        rowSelectionModel.length < selectionLimit ||
        rowSelectionModel.includes(params.id)
      );
    },
    [selectionLimit, rowSelectionModel],
  );

  return (
    <>
      {rowSelectionModel.length >= selectionLimit && (
        <Alert variant="standard" severity="warning" sx={{ my: 1 }}>
          <Typography>
            {t('selectionLimitWarning', { selectionLimit })}
          </Typography>
        </Alert>
      )}
      <DataGridPro
        columns={columns}
        apiRef={apiRef}
        getRowHeight={getRowHeight}
        loading={isLoading || isPersistenceLoading}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={rowExpansionContent}
        slots={slots}
        slotProps={slotProps}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        initialState={initialState}
        onRowsScrollEnd={handleOnRowsScrollEnd}
        hideFooterPagination
        scrollEndThreshold={300}
        hideFooterRowCount
        ignoreDiacritics
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        keepNonExistentRowsSelected
        hideFooterSelectedRowCount
        isRowSelectable={isRowSelectable}
        {...(getRowId ? { getRowId } : {})}
      />
    </>
  );
};
